import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Language as LanguageIcon,
  Computer as ComputerIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import cvService from '../../../services/cvService';
import { useTranslation } from 'react-i18next';

interface CVListItem {
  cvId?: number;
  id?: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  email: string;
  mobilePhone: string;
  desiredPosition: string;
  preferredDepartment?: string;
  city: string;
  birthDate?: string;
  age?: number;
  createdAt: string;
  applicationStatus?: string;
  educationLevel?: string;
  educationCount?: number;
  languageCount?: number;
  experienceYears?: number;
  totalExperienceYears?: number;
  desiredSalary?: number;
}

const RecruitmentManagementPage: React.FC = () => {
  const { t, i18n } = useTranslation(['humanResources', 'common2']);
  const hr = (key: string, defaultValue: string) => t(key, { ns: 'humanResources', defaultValue });
  // State Management
  const [cvList, setCvList] = useState<CVListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [selectedCV, setSelectedCV] = useState<any>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cvToDelete, setCvToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });

  // Fetch CV List
  useEffect(() => {
    fetchCVs();
  }, []);

  const fetchCVs = async () => {
    try {
      setLoading(true);
      const response = await cvService.getAllCVs();
      console.log('📥 Backend CV Response:', response);
      
      let cvData = [];
      
      if (response && response.success && response.data) {
        if (response.data.items) {
          cvData = response.data.items;
        } else if (Array.isArray(response.data)) {
          cvData = response.data;
        }
      } else if (Array.isArray(response)) {
        cvData = response;
      }
      
      setCvList(Array.isArray(cvData) ? cvData : []);
      
      console.log(`✅ ${cvData.length} ${hr('recruitment.logs.cvLoaded', 'CV records loaded')}`);
      
      if (cvData.length === 0) {
        setSnackbar({
          open: true,
          message: hr('recruitment.messages.noApplicationsYet', 'No CV applications have been submitted yet.'),
          severity: 'info'
        });
      }
    } catch (error: any) {
      console.error('❌ CV Listesi Hatası:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          hr('recruitment.errors.loadList', 'An error occurred while loading the CV list. Please check backend connectivity.');
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      setCvList([]);
    } finally {
      setLoading(false);
    }
  };

  // Search and Filter - Frontend'de filtreleme
  const handleSearch = () => {
    try {
      setPage(0);
      
      const filtered = getFilteredCVs();
      if (filtered.length === 0 && cvList.length > 0) {
        setSnackbar({
          open: true,
          message: hr('recruitment.messages.noSearchResults', 'No results found matching search criteria.'),
          severity: 'info'
        });
      }
    } catch (error: any) {
      console.error('Arama hatası:', error);
      setSnackbar({
        open: true,
        message: hr('recruitment.errors.search', 'An error occurred during search.'),
        severity: 'error'
      });
    }
  };

  // Clear Filters
  const handleClearFilters = () => {
    try {
      setSearchKeyword('');
      setFilterPosition('');
      setFilterCity('');
      setPage(0);
      
      setSnackbar({
        open: true,
        message: hr('recruitment.messages.filtersCleared', 'Filters cleared.'),
        severity: 'info'
      });
    } catch (error: any) {
      console.error('Filtre temizleme hatası:', error);
    }
  };

  // Filtrelenmiş liste - Güvenli veri kontrolü ile
  const getFilteredCVs = () => {
    if (!Array.isArray(cvList)) {
      console.warn('cvList bir array değil:', cvList);
      return [];
    }

    return cvList.filter(cv => {
      if (!cv) return false;

      const matchesKeyword = !searchKeyword || 
        (cv.firstName && cv.firstName.toLowerCase().includes(searchKeyword.toLowerCase())) ||
        (cv.lastName && cv.lastName.toLowerCase().includes(searchKeyword.toLowerCase())) ||
        (cv.fullName && cv.fullName.toLowerCase().includes(searchKeyword.toLowerCase())) ||
        (cv.email && cv.email.toLowerCase().includes(searchKeyword.toLowerCase())) ||
        (cv.mobilePhone && cv.mobilePhone.includes(searchKeyword));
      
      const matchesPosition = !filterPosition || 
        (cv.desiredPosition && cv.desiredPosition.toLowerCase().includes(filterPosition.toLowerCase()));
      
      const matchesCity = !filterCity || 
        (cv.city && cv.city.toLowerCase().includes(filterCity.toLowerCase()));

      return matchesKeyword && matchesPosition && matchesCity;
    });
  };

  const filteredCVs = getFilteredCVs();
  const paginatedCVs = filteredCVs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // View CV Detail
  const handleViewDetail = async (id: string) => {
    if (!id) {
      setSnackbar({
        open: true,
        message: hr('recruitment.errors.cvIdNotFound', 'CV ID not found.'),
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      const response = await cvService.getCVById(id);
      
      const cvData = response?.data || response;
      setSelectedCV(cvData);
      setDetailDialogOpen(true);
    } catch (error: any) {
      console.error('CV Detay Hatası:', error);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          hr('recruitment.errors.cvDetail', 'An error occurred while loading CV details.');
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete CV
  const handleDeleteClick = (id: string) => {
    if (!id) {
      setSnackbar({
        open: true,
        message: hr('recruitment.errors.cvIdNotFound', 'CV ID not found.'),
        severity: 'error'
      });
      return;
    }
    setCvToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!cvToDelete) {
      setSnackbar({
        open: true,
        message: hr('recruitment.errors.cvToDeleteNotFound', 'CV to be deleted not found.'),
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      console.log('🗑️ CV silme işlemi başlatılıyor, ID:', cvToDelete);
      
      await cvService.deleteCV(cvToDelete);
      
      console.log('✅ CV başarıyla silindi');
      setSnackbar({
        open: true,
        message: hr('recruitment.messages.cvDeleted', 'CV deleted successfully.'),
        severity: 'success'
      });
      
      await fetchCVs();
      
      setDeleteDialogOpen(false);
      setCvToDelete(null);
    } catch (error: any) {
      console.error('❌ CV Silme Hatası:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      let errorMessage = hr('recruitment.errors.cvDelete', 'An error occurred while deleting the CV.');
      
      if (error.response?.status === 405) {
        errorMessage = hr('recruitment.errors.cvDelete405', 'Delete operation is not supported (405). Please check backend API configuration.');
      } else if (error.response?.status === 404) {
        errorMessage = hr('recruitment.errors.cvDelete404', 'CV not found (404). It may have already been deleted.');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage = hr('recruitment.errors.unauthorized', 'You are not authorized for this action.');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      if (error.response?.status === 404) {
        setDeleteDialogOpen(false);
        setCvToDelete(null);
        await fetchCVs();
      }
    } finally {
      setLoading(false);
    }
  };

  // Pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format Date
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');
  };

  // Calculate Age
  const calculateAge = (birthDate: string) => {
    if (!birthDate) return '-';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative', overflowX: 'hidden' }}>
      <Box 
        sx={{ 
          height: 'calc(100vh - 80px)', 
          overflowY: 'auto', 
          overflowX: 'hidden',
          width: '100%',
          paddingRight: '10px',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
          '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '10px', '&:hover': { background: '#555' } }
        }}
      >
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          {/* Header */}
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center" 
            mb={4}
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 2, sm: 0 }}
          >
            <Box display="flex" alignItems="center">
              <PersonAddIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main', mr: 2 }} />
              <Typography 
                variant="h4" 
                component="h1" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {hr('recruitment.title', 'Recruitment Management')}
              </Typography>
            </Box>
          </Box>

          {/* Statistics Cards */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        {hr('recruitment.stats.totalApplications', 'Total Applications')}
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        {cvList.length}
                      </Typography>
                    </Box>
                    <PersonAddIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        {hr('recruitment.stats.thisWeek', 'This Week')}
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold" color="success.main">
                        {cvList.filter(cv => new Date(cv.createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
                      </Typography>
                    </Box>
                    <PersonAddIcon sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        {hr('recruitment.stats.distinctPositions', 'Distinct Positions')}
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold" color="info.main">
                        {new Set(cvList.map(cv => cv.desiredPosition)).size}
                      </Typography>
                    </Box>
                    <WorkIcon sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        {hr('recruitment.stats.distinctCities', 'Distinct Cities')}
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold" color="warning.main">
                        {new Set(cvList.map(cv => cv.city)).size}
                      </Typography>
                    </Box>
                    <LocationIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search and Filter Section */}
          <Paper sx={{ borderRadius: 2, boxShadow: 3, p: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder={hr('recruitment.filters.searchPlaceholder', 'Name, Surname, Email, or Phone...')}
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  size="small"
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={hr('recruitment.filters.positionPlaceholder', 'Filter by Position...')}
                  value={filterPosition}
                  onChange={(e) => setFilterPosition(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WorkIcon />
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={hr('recruitment.filters.cityPlaceholder', 'Filter by City...')}
                  value={filterCity}
                  onChange={(e) => setFilterCity(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon />
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    {loading ? hr('recruitment.actions.searching', 'Searching...') : hr('recruitment.actions.search', 'Search')}
                  </Button>
                  <Tooltip title={hr('recruitment.actions.clearFilters', 'Clear Filters')}>
                    <span>
                      <IconButton 
                        onClick={handleClearFilters} 
                        color="secondary"
                        disabled={loading}
                      >
                        <FilterListIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* CV Table */}
          <Paper sx={{ borderRadius: 2, boxShadow: 3 }}>
            <Box p={3}>
              <Typography variant="h6" component="h2" fontWeight="bold" mb={2}>
                {hr('recruitment.cvList', 'CV List')} ({filteredCVs.length} / {cvList.length})
              </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
              </Box>
            ) : cvList.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <PersonAddIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  {hr('recruitment.empty.noApplications', 'No CV applications yet.')}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {hr('recruitment.empty.noApplicationsHint', 'New applications will be displayed here when they arrive.')}
                </Typography>
              </Box>
            ) : filteredCVs.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <SearchIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  {hr('recruitment.empty.noSearchResults', 'No results found matching your search criteria.')}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {hr('recruitment.empty.noSearchResultsHint', 'Please try again with different criteria.')}
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={handleClearFilters}
                  sx={{ mt: 2 }}
                  startIcon={<FilterListIcon />}
                >
                  {hr('recruitment.actions.clearFilters', 'Clear Filters')}
                </Button>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ 
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                  overflow: 'auto',
                  maxHeight: '70vh'
                }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 150
                        }}>{hr('recruitment.table.fullName', 'Full Name')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 180
                        }}>{hr('recruitment.table.contact', 'Contact')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 130
                        }}>{hr('recruitment.table.position', 'Position')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 100
                        }}>{hr('recruitment.table.city', 'City')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 60
                        }}>{hr('recruitment.table.age', 'Age')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 100
                        }}>{hr('recruitment.table.applicationDate', 'Application Date')}</TableCell>
                        <TableCell align="center" sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 80
                        }}>{hr('recruitment.table.actions', 'Actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCVs.map((cv) => {
                          const cvId = cv.cvId?.toString() || cv.id || '';
                          return (
                          <TableRow 
                            key={cvId} 
                            hover
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                              '& .MuiTableCell-root': { 
                                py: 0.5,
                                height: 32,
                                fontSize: '0.775rem'
                              }
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {cv.firstName} {cv.lastName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <EmailIcon fontSize="small" sx={{ fontSize: 14 }} color="action" />
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{cv.email}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PhoneIcon fontSize="small" sx={{ fontSize: 14 }} color="action" />
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{cv.mobilePhone}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={cv.desiredPosition || hr('recruitment.notSpecified', 'Not Specified')} 
                                color="primary" 
                                size="small" 
                                sx={{ fontSize: '11px', height: 24 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.75rem',
                                color: !cv.city ? 'text.secondary' : 'text.primary'
                              }}>
                                {cv.city || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {cv.birthDate ? calculateAge(cv.birthDate) : (cv.age || '-')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(cv.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={hr('recruitment.actions.viewDetails', 'View Details')}>
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleViewDetail(cvId)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={hr('common.delete', 'Delete')}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(cvId)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredCVs.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={hr('recruitment.rowsPerPage', 'Rows per page:')}
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                />
              </>
            )}
            </Box>
          </Paper>

          {/* CV Detail Dialog */}
          <Dialog
            open={detailDialogOpen}
            onClose={() => setDetailDialogOpen(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: { maxHeight: '90vh' }
            }}
          >
            <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PersonIcon sx={{ fontSize: 32 }} />
                  <Typography variant="h5">{hr('recruitment.cvDetails', 'CV Details')}</Typography>
                </Box>
                <IconButton onClick={() => setDetailDialogOpen(false)} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              {selectedCV && (
                <Box>
                  {/* Kişisel Bilgiler */}
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6" fontWeight="bold">
                        <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {hr('recruitment.sections.personalInfo', 'Personal Information')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.table.fullName', 'Full Name')}</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedCV.firstName} {selectedCV.lastName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.fields.nationalId', 'National ID No')}</Typography>
                          <Typography variant="body1">{selectedCV.tcIdentityNo || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">Email</Typography>
                          <Typography variant="body1">{selectedCV.email}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.fields.phone', 'Phone')}</Typography>
                          <Typography variant="body1">{selectedCV.mobilePhone}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.fields.birthDate', 'Birth Date')}</Typography>
                          <Typography variant="body1">{formatDate(selectedCV.birthDate)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.table.city', 'City')}</Typography>
                          <Typography variant="body1">{selectedCV.city}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.fields.address', 'Address')}</Typography>
                          <Typography variant="body1">{selectedCV.homeAddress || '-'}</Typography>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* İş Tercihleri */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6" fontWeight="bold">
                        <WorkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {hr('recruitment.sections.jobPreferences', 'Job Preferences')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.fields.desiredPosition', 'Desired Position')}</Typography>
                          <Chip label={selectedCV.desiredPosition} color="primary" sx={{ mt: 1 }} />
                        </Grid>
                        
                        {/* Güvenlik Kartı Bitiş Tarihi - Sadece Güvenlik pozisyonu için göster */}
                        {selectedCV.desiredPosition === 'Güvenlik' && selectedCV.securityCardExpiryDate && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                              {hr('recruitment.fields.securityCardExpiry', '🔐 Security Card Expiry Date')}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium" sx={{ mt: 1 }}>
                              {formatDate(selectedCV.securityCardExpiryDate)}
                            </Typography>
                          </Grid>
                        )}
                        
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.fields.expectedSalary', 'Expected Salary')}</Typography>
                          <Typography variant="body1">{selectedCV.desiredSalary ? `${selectedCV.desiredSalary} ${hr('recruitment.currency', 'TRY')}` : '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.fields.nightShift', 'Night Shift')}</Typography>
                          <Chip label={selectedCV.shiftWorkAcceptable ? hr('recruitment.accepted', 'Accepted') : hr('recruitment.rejected', 'Rejected')} 
                                color={selectedCV.shiftWorkAcceptable ? 'success' : 'error'} 
                                size="small" sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.fields.travel', 'Travel')}</Typography>
                          <Chip label={selectedCV.canTravel ? hr('recruitment.accepted', 'Accepted') : hr('recruitment.rejected', 'Rejected')} 
                                color={selectedCV.canTravel ? 'success' : 'error'} 
                                size="small" sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.fields.overtime', 'Overtime')}</Typography>
                          <Chip label={selectedCV.overtimeAcceptable ? hr('recruitment.accepted', 'Accepted') : hr('recruitment.rejected', 'Rejected')} 
                                color={selectedCV.overtimeAcceptable ? 'success' : 'error'} 
                                size="small" sx={{ mt: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">{hr('recruitment.fields.relocation', 'Relocation')}</Typography>
                          <Chip label={selectedCV.canRelocate ? hr('recruitment.accepted', 'Accepted') : hr('recruitment.rejected', 'Rejected')} 
                                color={selectedCV.canRelocate ? 'success' : 'error'} 
                                size="small" sx={{ mt: 1 }} />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Eğitim Bilgileri */}
                  {selectedCV.educations && selectedCV.educations.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" fontWeight="bold">
                          <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          {hr('recruitment.sections.education', 'Education')} ({selectedCV.educations.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {selectedCV.educations.map((edu: any, index: number) => (
                          <Card key={index} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                            <CardContent>
                              <Typography variant="subtitle1" fontWeight="bold">{edu.schoolName}</Typography>
                              <Typography variant="body2">{edu.department}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {edu.startYear} - {edu.endYear} | {edu.educationLevel}
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* İş Deneyimi */}
                  {selectedCV.experiences && selectedCV.experiences.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" fontWeight="bold">
                          <WorkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          {hr('recruitment.sections.workExperience', 'Work Experience')} ({selectedCV.experiences.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {selectedCV.experiences.map((exp: any, index: number) => (
                          <Card key={index} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                            <CardContent>
                              <Typography variant="subtitle1" fontWeight="bold">{exp.position}</Typography>
                              <Typography variant="body2">{exp.companyName}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {formatDate(exp.startDate)} - {exp.isCurrent ? hr('recruitment.ongoing', 'Ongoing') : formatDate(exp.endDate)}
                              </Typography>
                              {exp.jobDescription && (
                                <Typography variant="body2" sx={{ mt: 1 }}>{exp.jobDescription}</Typography>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Dil Bilgisi */}
                  {selectedCV.languages && selectedCV.languages.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" fontWeight="bold">
                          <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          {hr('recruitment.sections.languages', 'Languages')} ({selectedCV.languages.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {selectedCV.languages.map((lang: any, index: number) => (
                            <Grid item xs={12} sm={6} key={index}>
                              <Card sx={{ bgcolor: 'grey.50' }}>
                                <CardContent>
                                  <Typography variant="subtitle2" fontWeight="bold">{lang.languageName}</Typography>
                                  <Chip label={lang.proficiencyLevel} size="small" color="info" sx={{ mt: 1 }} />
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Bilgisayar Becerileri */}
                  {selectedCV.computerSkills && selectedCV.computerSkills.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" fontWeight="bold">
                          <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          {hr('recruitment.sections.computerSkills', 'Computer Skills')} ({selectedCV.computerSkills.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {selectedCV.computerSkills.map((skill: any, index: number) => (
                            <Grid item xs={12} sm={6} key={index}>
                              <Card sx={{ bgcolor: 'grey.50' }}>
                                <CardContent>
                                  <Typography variant="subtitle2" fontWeight="bold">{skill.programName}</Typography>
                                  <Chip label={skill.proficiencyLevel} size="small" color="secondary" sx={{ mt: 1 }} />
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setDetailDialogOpen(false)} variant="contained">
                {hr('common.close', 'Close')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>{hr('recruitment.deleteConfirmTitle', 'CV Deletion Confirmation')}</DialogTitle>
            <DialogContent>
              <Typography>
                {hr('recruitment.deleteConfirmMessage', 'Are you sure you want to delete this CV? This action cannot be undone.')}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
                {hr('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>
                {loading ? hr('recruitment.deleting', 'Deleting...') : hr('common.delete', 'Delete')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert 
              severity={snackbar.severity} 
              onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </Box>
  );
};

export default RecruitmentManagementPage;
